import type { Profile, ProfileComponent } from '@nx/domain';
import {
  DEFAULT_THEME_CONFIG,
  normalizeProfileOptionalText,
  normalizeProfileRoleType,
} from '@nx/domain';

export function mapPreviewPayload(payload: {
  profile: Record<string, unknown> & { id: string };
  components: Array<Record<string, unknown> & { id: string }>;
}): { profile: Profile; components: ProfileComponent[] } {
  const p = payload.profile;
  const themeRaw = (p.themeConfig ?? {}) as Record<string, unknown>;
  const profile: Profile = {
    id: payload.profile.id,
    ownerId: String(p.ownerId ?? ''),
    slug: String(p.slug ?? ''),
    title: String(p.title ?? ''),
    description: (p.description as string | undefined) ?? null,
    tagline: normalizeProfileOptionalText(p.tagline as string | undefined),
    location: normalizeProfileOptionalText(p.location as string | undefined),
    avatarUrl: (p.avatarUrl as string | undefined) ?? null,
    roleType: normalizeProfileRoleType(p.roleType),
    tags: Array.isArray(p.tags) ? (p.tags as string[]) : [],
    status: (p.status as Profile['status']) ?? 'draft',
    themeConfig: {
      ...DEFAULT_THEME_CONFIG,
      templateId: String(themeRaw.templateId ?? DEFAULT_THEME_CONFIG.templateId),
      primaryColor: String(themeRaw.primaryColor ?? DEFAULT_THEME_CONFIG.primaryColor),
      backgroundColor: (themeRaw.backgroundColor as string | undefined) ?? null,
      backgroundImageUrl: (themeRaw.backgroundImageUrl as string | undefined) ?? null,
      fontStyle: (themeRaw.fontStyle as Profile['themeConfig']['fontStyle']) ?? 'sans',
      buttonRadius:
        typeof themeRaw.buttonRadius === 'number'
          ? themeRaw.buttonRadius
          : DEFAULT_THEME_CONFIG.buttonRadius,
      buttonColor: String(themeRaw.buttonColor ?? DEFAULT_THEME_CONFIG.buttonColor),
    },
    viewCount: typeof p.viewCount === 'number' ? p.viewCount : 0,
    createdAt: null,
    updatedAt: null,
  };

  const components: ProfileComponent[] = payload.components.map((c) => {
    const config = (c.config ?? {}) as Record<string, unknown>;
    return {
      id: c.id,
      profileId: profile.id,
      type: (c.type as ProfileComponent['type']) ?? 'custom_link',
      config: {
        platform: config.platform as ProfileComponent['config']['platform'],
        url: config.url as string | undefined,
        label: config.label as string | undefined,
        content: config.content as string | undefined,
        imageUrl: config.imageUrl as string | undefined,
        embedId: config.embedId as string | undefined,
        handle: config.handle as string | undefined,
        note: config.note as string | undefined,
        caption: config.caption as string | undefined,
      },
      visible: c.visible !== false,
      sortOrder: typeof c.sortOrder === 'number' ? c.sortOrder : 0,
      clickCount: typeof c.clickCount === 'number' ? c.clickCount : 0,
      createdAt: null,
    };
  });

  return { profile, components };
}
